/**
 * PersonPicker — inline-editable person assignment for demand sidebar.
 *
 * Shows current assignee with an edit button. On click, opens a
 * combobox-style dropdown to pick a user from the mentionable list.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  makeStyles,
  tokens,
  Button,
  Input,
} from '@fluentui/react-components'
import { Edit16Regular, Dismiss16Regular } from '@fluentui/react-icons'
import type { MentionableUser } from '../hooks/useMentionableUsers'
import { bayerColors } from '../theme'

const useStyles = makeStyles({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 0',
  },
  avatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 700,
    color: '#fff',
    flexShrink: 0,
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontSize: '13px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  role: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  editBtn: {
    opacity: 0.5,
    ':hover': { opacity: 1 },
  },
  pickerWrapper: {
    position: 'relative',
    marginTop: '4px',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    maxHeight: '160px',
    overflowY: 'auto',
    background: tokens.colorNeutralBackground1,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow8,
    zIndex: 100,
    marginTop: '2px',
  },
  option: {
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ':hover': {
      background: tokens.colorNeutralBackground1Hover,
    },
  },
  optionActive: {
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: tokens.colorBrandBackground2,
  },
  optionName: {
    fontWeight: 500,
  },
  optionRole: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    marginLeft: 'auto',
  },
  empty: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    padding: '6px 10px',
    fontStyle: 'italic',
  },
  clearRow: {
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    ':hover': { background: tokens.colorNeutralBackground1Hover },
  },
})

interface PersonPickerProps {
  currentName: string | null | undefined
  userId?: number | null
  roleLabel: string
  avatarColor?: string
  users: MentionableUser[]
  disabled?: boolean
  allowFreehand?: boolean
  onSelect: (user: MentionableUser | null) => void
  onFreehandSelect?: (name: string) => void
  /** What an empty slot says. Defaults to "No {roleLabel} assigned", which
   *  reads as nonsense when the label is an action ("No add contributor
   *  assigned", #1378). */
  emptyLabel?: string
  /** When set, an empty slot offers a one-click "Assign to me" — taking
   *  ownership used to mean typing your own name into a search box. */
  selfUser?: { id: number; name: string } | null
}

export function PersonPicker({
  currentName,
  userId,
  roleLabel,
  avatarColor = bayerColors.navy,
  users,
  disabled,
  allowFreehand,
  onSelect,
  onFreehandSelect,
  emptyLabel,
  selfUser,
}: PersonPickerProps) {
  const styles = useStyles()
  const navigate = useNavigate()
  const [editing, setEditing] = useState(false)
  const [search, setSearch] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const pickerRef = useRef<HTMLDivElement>(null)

  const filtered = users.filter((u) =>
    u.name.toLowerCase().includes(search.toLowerCase()),
  )

  useEffect(() => {
    setActiveIndex(0)
  }, [search])

  useEffect(() => {
    if (editing) {
      // Fluent UI Input may not forward ref to native <input>; query DOM as fallback
      setTimeout(() => {
        const el = inputRef.current ?? pickerRef.current?.querySelector('input')
        el?.focus()
      }, 50)
    }
  }, [editing])

  const handleSelect = useCallback(
    (user: MentionableUser | null) => {
      onSelect(user)
      setEditing(false)
      setSearch('')
    },
    [onSelect],
  )

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered.length > 0) {
        const user = filtered[activeIndex]
        if (user) handleSelect(user)
      } else if (allowFreehand && search.trim()) {
        onFreehandSelect?.(search.trim())
        setEditing(false)
        setSearch('')
      }
    } else if (e.key === 'Escape') {
      setEditing(false)
      setSearch('')
    }
  }

  function getInitials(name: string) {
    return name
      .split(' ')
      .map((p) => p[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
  }

  if (editing) {
    return (
      <div className={styles.pickerWrapper} ref={pickerRef}>
        <div style={{ display: 'flex', gap: '4px' }}>
          <Input
            ref={inputRef}
            size="small"
            value={search}
            onChange={(_e, d) => setSearch(d.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => setTimeout(() => { setEditing(false); setSearch('') }, 150)}
            placeholder={`Search ${roleLabel.toLowerCase()}…`}
            style={{ flex: 1 }}
          />
          <Button
            size="small"
            appearance="subtle"
            icon={<Dismiss16Regular />}
            onClick={() => { setEditing(false); setSearch('') }}
          />
        </div>
        <div className={styles.dropdown}>
          {currentName && (
            <div
              className={styles.clearRow}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(null) }}
            >
              ✕ Remove {roleLabel.toLowerCase()}
            </div>
          )}
          {filtered.length === 0 && !allowFreehand && (
            <div className={styles.empty}>No matching users</div>
          )}
          {filtered.length === 0 && allowFreehand && search.trim() && (
            <div
              className={styles.option}
              onMouseDown={(e) => {
                e.preventDefault()
                onFreehandSelect?.(search.trim())
                setEditing(false)
                setSearch('')
              }}
            >
              <span className={styles.optionName}>Use "{search.trim()}"</span>
              <span className={styles.optionRole}>freehand</span>
            </div>
          )}
          {filtered.length === 0 && allowFreehand && !search.trim() && (
            <div className={styles.empty}>Type a name…</div>
          )}
          {filtered.map((user, i) => (
            <div
              key={user.id}
              className={i === activeIndex ? styles.optionActive : styles.option}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(user) }}
            >
              <span className={styles.optionName}>{user.name}</span>
              <span className={styles.optionRole}>{user.role}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!currentName) {
    return (
      <div className={styles.row}>
        <div className={styles.info}>
          <div className={styles.role} style={{ fontStyle: 'italic' }}>
            {emptyLabel ?? `No ${roleLabel.toLowerCase()} assigned`}
          </div>
        </div>
        {!disabled && selfUser && (
          <Button
            size="small"
            appearance="subtle"
            data-testid={`assign-to-me-${roleLabel.toLowerCase()}`}
            onClick={() =>
              onSelect({ id: selfUser.id, name: selfUser.name, email: '', role: '' })
            }
          >
            Assign to me
          </Button>
        )}
        {!disabled && (
          <Button
            size="small"
            appearance="subtle"
            icon={<Edit16Regular />}
            className={styles.editBtn}
            onClick={() => setEditing(true)}
            title={`Assign ${roleLabel.toLowerCase()}`}
          />
        )}
      </div>
    )
  }

  return (
    <div className={styles.row}>
      <div className={styles.avatar} style={{ background: avatarColor }}>
        {getInitials(currentName)}
      </div>
      <div className={styles.info}>
        <div
          className={styles.name}
          style={userId ? { cursor: 'pointer', color: bayerColors.navyMid } : undefined}
          onClick={userId ? () => navigate(`/talents/${userId}`) : undefined}
          role={userId ? 'link' : undefined}
          tabIndex={userId ? 0 : undefined}
          onKeyDown={userId ? (e) => e.key === 'Enter' && navigate(`/talents/${userId}`) : undefined}
        >
          {currentName}
        </div>
        <div className={styles.role}>{roleLabel}</div>
      </div>
      {!disabled && (
        <Button
          size="small"
          appearance="subtle"
          icon={<Edit16Regular />}
          className={styles.editBtn}
          onClick={() => setEditing(true)}
          title={`Change ${roleLabel.toLowerCase()}`}
        />
      )}
    </div>
  )
}
